import {
  ApplicationConfig,
  provideZoneChangeDetection,
  isDevMode,
  inject,
  PLATFORM_ID,
  ErrorHandler,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';

import { routes } from './app.routes';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { getApp, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  connectFirestoreEmulator,
  initializeFirestore,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import {
  AUTH,
  APP_CHECK,
  FIREBASE_APP,
  FIRESTORE,
  FUNCTIONS,
} from 'src/app/common/firebase/firebase.tokens';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { isPlatformServer } from '@angular/common';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import {
  AccountEffects,
  accountFeatureKey,
  accountReducer,
  AgencyEffects,
  agencyFeatureKey,
  agencyReducer,
  AgentEffects,
  agentFeatureKey,
  agentReducer,
  EmployeeEffects,
  employeeFeatureKey,
  employeeReducer,
  PayoutEffects,
  payoutFeatureKey,
  payoutReducer,
  PermissionGrantedEffects,
  permissionGrantedFeatureKey,
  permissionGrantedReducer,
  StaticEffects,
  staticFeatureKey,
  staticReducer,
} from './shared/store';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { initializeWhiteLabel } from './shared/initializers/wl.initializer';
import { WlService } from 'src/app/common/services/wl.service';
import {
  QueryClient,
  provideTanStackQuery,
} from '@tanstack/angular-query-experimental';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { provideEnrollhereApiClient } from './common/enrollhere-api/orval-mutator';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
if (!environment.production) {
  // make tanstack query client visible for the chrome extension
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).__TANSTACK_QUERY_CLIENT__ = queryClient;
}

const provideSentryErrorHandler = (opts = {}) => {
  return {
    provide: ErrorHandler,
    useValue: createErrorHandler({
      ...opts,
    }),
  };
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideClientHydration(withEventReplay()),
    {
      provide: FIREBASE_APP,
      useFactory: () => initializeApp(environment.firebase),
    },
    {
      provide: AUTH,
      useFactory: (app: ReturnType<typeof initializeApp>) => getAuth(app),
      deps: [FIREBASE_APP],
    },
    {
      provide: APP_CHECK,
      useFactory: (
        app: ReturnType<typeof initializeApp>,
        platformId: object
      ) => {
        if (isPlatformServer(platformId)) {
          return;
        }
        const provider = new ReCaptchaEnterpriseProvider(
          environment.firebase.appCheckRecaptchaSiteKey
        );
        return initializeAppCheck(app, {
          provider,
          isTokenAutoRefreshEnabled: true,
        });
      },
      deps: [FIREBASE_APP, PLATFORM_ID],
    },
    {
      provide: FIRESTORE,
      useFactory: () => {
        const firestore = initializeFirestore(getApp(), {
          ignoreUndefinedProperties: true,
        });
        if (environment.useEmulators.firestore) {
          connectFirestoreEmulator(firestore, 'localhost', 8080);
        }
        return firestore;
      },
    },
    {
      provide: FUNCTIONS,
      useFactory: () => {
        const functions = getFunctions(undefined, 'us-east1');
        if (environment.useEmulators.functions) {
          connectFunctionsEmulator(functions, 'localhost', 5001);
        }
        return functions;
      },
    },
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideEnrollhereApiClient(() => ({
      baseUrl: environment.api.endpoint,
    })),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideStore({
      [accountFeatureKey]: accountReducer,
      [agentFeatureKey]: agentReducer,
      [agencyFeatureKey]: agencyReducer,
      [employeeFeatureKey]: employeeReducer,
      [payoutFeatureKey]: payoutReducer,
      [permissionGrantedFeatureKey]: permissionGrantedReducer,
      [staticFeatureKey]: staticReducer,
    }),
    provideEffects([
      AccountEffects,
      AgentEffects,
      AgencyEffects,
      EmployeeEffects,
      PayoutEffects,
      PermissionGrantedEffects,
      StaticEffects,
    ]),
    provideSentryErrorHandler({
      showDialog: false,
    }),
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(TraceService);
    }),
    provideAppInitializer(() => {
      inject(WlService);
      return initializeWhiteLabel()();
    }),
    provideTanStackQuery(queryClient),
  ],
};
